import {Form, Input, Select, Switch} from 'antd';
import {fieldRules} from 'utils/helper';
import styles from '../rewardSchemeTabs/newRewardSchemes.module.scss';

/**
 * Which instrument a scheme hands out, and how many units.
 *
 * The selector is `instrumentId`, not `ticker`. `RewardConfigStockInput` no
 * longer carries a ticker field at all, so a scheme configured by ticker
 * cannot be saved — everything is an instrument now, and the reward config
 * follows the same vocabulary as the rest of the platform.
 *
 * A ticker is no longer unique either: the same symbol can list on two
 * exchanges, so the option shows the exchange alongside it.
 */
export const StocksRewardConfig = ({
  hasReward,
  randomStock,
  loading,
  stockOptions,
  legacyTicker,
}) => {
  if (!hasReward) {
    return null;
  }

  const options = stockOptions ?? [];
  // Banks created before the instrument migration have no `instrumentId`.
  // They cannot be sent, so they are shown and disabled rather than hidden —
  // an operator looking for a ticker they know exists needs to see why it is
  // unavailable, not find it missing.
  const unbacked = options.filter(item => !item?.instrumentId).length;

  return (
    <div className={styles.configSection}>
      <div className={styles.sectionHeader}>
        <h1>Instrument Reward Configuration</h1>
      </div>
      <Form.Item
        label="Pick Instrument Randomly"
        className={styles.field}
        name="randomStock"
        valuePropName="checked">
        <Switch />
      </Form.Item>
      <div className={styles['date-format']}>
        {!randomStock && (
          <Form.Item
            label="Instrument"
            className={styles.field}
            name="instrumentId"
            extra={
              legacyTicker
                ? `Previously set to ${legacyTicker}, which was stored without an instrument reference. Pick it again to save this scheme.`
                : unbacked > 0
                ? `${unbacked} reward bank${
                    unbacked === 1 ? '' : 's'
                  } cannot be selected yet — they have no instrument linked.`
                : undefined
            }
            rules={[fieldRules('instrument is required')]}>
            <Select
              showSearch
              allowClear
              optionFilterProp="children"
              loading={loading}>
              {options.map((item, index) => (
                <Select.Option
                  /**
                   * Unlinked banks need a value that cannot collide. Left as
                   * `undefined` they all shared one, so antd matched an empty
                   * form value to an arbitrary option — a scheme whose ticker
                   * was ETIT rendered as "SIBC" selected. A sentinel keyed on
                   * the bank id can never equal a real instrument id, so an
                   * unset field stays visibly unset.
                   */
                  value={item?.instrumentId ?? `unlinked:${item?.id ?? index}`}
                  disabled={!item?.instrumentId}
                  key={item?.id ?? `${item?.ticker}-${index}`}>
                  {item?.ticker}
                  {item?.exchangeCode ? ` · ${item.exchangeCode}` : ''}
                  {item?.instrumentId ? '' : ' — no instrument linked'}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item
          label="Units"
          className={styles.field}
          name="units"
          rules={[fieldRules('unit is required')]}>
          <Input type="number" min={1} />
        </Form.Item>
      </div>
    </div>
  );
};
